import React from 'react';
import {Size} from '../types';
import styled from 'styled-components';
import {COLORS, ComponentLStyling, ComponentMStyling, ComponentSStyling, ComponentTextStyle} from '../styles';

export const Bar = styled.div`
  flex: 1;
  height: 6px;

  &.small {
    height: 6px;
  }

  &.medium {
    height: 8px;
  }

  &.large {
    height: 12px;
  }

  background-color: ${COLORS.neutral_100};

  &.active {
    background-color: ${COLORS.accent1_400};
  }

  &.current {
    background-color: ${COLORS.accent1_800};
  }

  &.inverted {
    background-color: ${COLORS.neutral_600};
  }

  &.inverted.active {
    background-color: ${COLORS.accent1_400};
  }

  &.inverted.current {
    background-color: ${COLORS.white};
  }

`;

export const Dot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;


  &:after {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${COLORS.neutral_400};
  }

  &.small {
    &:after {
      width: 6px;
      height: 6px;
    }

    &.current:after {
      width: 10px;
      height: 10px;
    }
  }

  &.medium {
    &:after {
      width: 8px;
      height: 8px;
    }

    &.current:after {
      width: 12px;
      height: 12px;
    }
  }

  &.large {
    &:after {
      width: 12px;
      height: 12px;
    }

    &.current:after {
      width: 16px;
      height: 16px;
    }
  }


  &.active:after {
    background-color: ${COLORS.accent1_600};
  }

  &.current:after {
    background-color: ${COLORS.accent1_800};
  }

  &.inverted:after {
    background-color: ${COLORS.neutral_400};
  }

  &.inverted.active:after {
    background-color: ${COLORS.accent1_400};
  }

  &.inverted.current:after {
    background-color: ${COLORS.white};
  }
`;

export const LineFill = styled.div`
  height: 100%;
  max-width: 100%;
  position: absolute;
  z-index: 1;
`;

export const Line = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;

  &:after {
    content: '';
    display: block;
    height: 100%;
    background-color: ${COLORS.neutral_500};
    position: absolute;
    right: 0;
  }

  &:before {
    content: '';
    display: block;
    height: 100%;
    background-color: ${COLORS.neutral_500};
    position: absolute;
    left: 0;
  }

  &.small {
    height: 6px;
    border-radius: 4px;

    ${LineFill} {
      border-radius: 4px;
    }

    &:after {
      width: 3px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }

    &:before {
      width: 3px;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }

  &.medium {
    height: 8px;
    border-radius: 4px;

    ${LineFill} {
      border-radius: 4px;
    }

    &:after {
      width: 4px;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }

    &:before {
      width: 4px;
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
  }

  &.large {
    height: 12px;
    border-radius: 6px;

    ${LineFill} {
      border-radius: 6px;
    }

    &:after {
      width: 6px;
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
    }

    &:before {
      width: 6px;
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }
  }

  background-color: ${COLORS.neutral_100};

  ${LineFill} {
    background-color: ${COLORS.accent1_500};
  }

  &.inverted {
    background-color: ${COLORS.neutral_700};

    &:after,
    &:before {
      background-color: ${COLORS.neutral_400};
    }

    ${LineFill} {
      background-color: ${COLORS.accent1_400};
    }
  }
`;

const Label = styled.div`
  color: ${COLORS.black};
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  height: 16px;
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.inverted {
    ${Label} {
      color: ${COLORS.white};
    }
  }

  &.small {
    gap: 4px;

    ${Label} {
      ${ComponentSStyling(ComponentTextStyle.Regular, null)}
    }

    ${Content} {
      gap: 4px;

      ${Bar}:first-of-type {
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
      }

      ${Bar}:last-of-type {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
      }
    }
  }

  &.medium {
    ${Label} {
      ${ComponentMStyling(ComponentTextStyle.Regular, null)}
    }

    ${Content} {
      gap: 6px;

      ${Bar}:first-of-type {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }

      ${Bar}:last-of-type {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
      }
    }
  }

  &.large {
    ${Label} {
      ${ComponentLStyling(ComponentTextStyle.Regular, null)}
    }

    ${Content} {
      gap: 8px;

      ${Bar}:first-of-type {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
      }

      ${Bar}:last-of-type {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
      }
    }
  }

  &.dots.small,
  &.dots.medium {
    ${Content} {
      padding: 0 2px;
    }
  }

  &.dots.large {
    ${Content} {
      padding: 0 3px;
    }
  }
`;

export enum LinearProgressType {
  Dots = 'dots',
  Line = 'line',
  Bars = 'bars',
}

export enum LinearProgressVariant {
  Normal = 'normal',
  Inverted = 'inverted',
}

export interface LinearProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: Size.Small | Size.Medium | Size.Large;
  type?: LinearProgressType;
  variant?: LinearProgressVariant;

  label?: string;

  value: number;
  max: number;
}

const LinearProgress: React.FunctionComponent<LinearProgressProps> = ({
                                                                        size = Size.Medium,
                                                                        type = LinearProgressType.Line,
                                                                        variant = LinearProgressVariant.Normal,
                                                                        label,
                                                                        value,
                                                                        max,
                                                                        className,
                                                                        ...rest
                                                                      }) => {

  const renderBars = () =>
    <>
      {Array.from(Array(max).keys()).map(i => (
        <Bar key={i}
             className={`${Math.ceil(value) >= i + 1 ? 'active' : ''} ${Math.ceil(value) === i + 1 ? 'current' : ''} ${variant} ${size}`}/>
      ))}
    </>

  const renderDots = () =>
    <>
      {Array.from(Array(max).keys()).map(i => (
        <Dot key={i}
             className={`${Math.ceil(value) >= i + 1 ? 'active' : ''} ${Math.ceil(value) === i + 1 ? 'current' : ''} ${variant} ${size}`}/>
      ))}
    </>

  const renderLine = () =>
    <>
      <Line className={`${variant} ${size}`}>
        <LineFill style={{width: `${value / max * 100}%`}}/>
      </Line>
    </>


  const render = () => {
    switch (type) {
      case LinearProgressType.Bars:
        return renderBars();
      case LinearProgressType.Dots:
        return renderDots();
      case LinearProgressType.Line:
      default:
        return renderLine();
    }
  }

  return (
    <Wrapper className={`${size} ${type} ${variant} ${className || ''}`} {...rest}>
      {label && <Label>{label}</Label>}
      <Content>
        {render()}
      </Content>
    </Wrapper>
  )
};

export default LinearProgress;
